import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	CartAndSystemValidationModal,
	CartDrawer,
	Contact,
	ContentLoader,
	ErrorCrossIcon,
	FooterButtonNav,
	MiniCartIcon,
	Toast,
} from '@adac-mp/components';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import {
	CartService,
	DkvContext,
	ICartData,
	IDkvContext,
	IItemInfo,
	IOffer,
	IOfferContext,
	IVignetteData,
	OfferContext,
	VignetteContext,
	countryUrlMap,
	getErrorTextFromResponse,
	getFocusableElements,
	getProductByOfferHash,
	getVignetteData,
	setRouteLangParam,
	validationErrorCodes,
	getLabelValue,
	HeaderHeights,
	calculateCurrentStep,
	ICartOverlapItem,
	IUpsellRequestBody,
	initDeepLinkAutoScroll,
	getUrlBasePath,
} from '@adac-mp/shared';
import { redirectToErrorPage } from '@adac-mp/shared/src/services/axios-config';
import { gatherProductTrackingData } from '@adac-mp/components/src/utils/tracking/tracking';
import { usePreviousValue } from '@adac-mp/components/src/hooks/usePreviousValue';

interface IConfigurationProps {
	componentList: {
		component: React.ReactElement;
		title: string;
	}[];
	reuse?: boolean;
	supplierHasMultipleProducts?: boolean;
	supplierId: number;
	withStepReset?: boolean;
	onFinished: () => void;
	setReuse?: (bool: boolean) => void;
}

const Configuration = ({
	componentList,
	reuse = false,
	supplierHasMultipleProducts,
	supplierId,
	withStepReset = false,
	onFinished,
	setReuse = () => {},
}: IConfigurationProps) => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const {
		productsTypeMap,
		selectedProducts,
		selectedSectionTolls,
		selectedCountries,
		cartItems,
		cartPrice,
		platePubliclyVisible,
		updateVehicleCategory,
		updateSelectedCartItem,
		updateCart,
		deleteSelectedProducts,
	} = useContext(VignetteContext);
	const { vehicleRegistration } = useContext<IDkvContext>(DkvContext);
	const { lang, allOffers, labels } = useContext<IOfferContext>(OfferContext);
	const { viewport } = useViewport();
	const sectionsRefArray = useRef<HTMLElement[]>([]);
	const currElRef = useRef<HTMLElement | null>(null);
	const lastStep = componentList.length - 1;
	const initialStepsValidationMap = componentList.map(() => false);
	const [autoScroll, setAutoscroll] = useState(initDeepLinkAutoScroll(searchParams));
	const [validatedStepsMap, setValidatedStepsMap] = useState(componentList.map(() => false));
	const [step, setStep] = useState<number>(selectedProducts.length || vehicleRegistration ? lastStep : 0);
	const [errorStepCount, setErrorStepCount] = useState<number | undefined>();
	const [miniCartIsOpen, setMiniCartIsOpen] = useState(false);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [validationErrorCode, setValidationErrorCode] = useState<number | undefined>(undefined);
	const [cartOverlapItems, setCartOverlapItems] = useState<ICartOverlapItem[] | []>([]);
	const currSelectedProduct = selectedProducts[0];
	const hasFlexService = productsTypeMap.get('flexProduct');
	const isOneCartFlow = supplierId === 0;
	const isDkvFlow = supplierId === 5;
	const previousStep = usePreviousValue(step);
	const errorIndex = validatedStepsMap.findIndex((bool) => !bool);
	const hasErrorIndex = errorIndex > -1 && errorIndex < step + 1;

	const currentUrlStepParam = Number(searchParams.get('step'));
	const [urlStepParam, setUrlStepParam] = useState(1);

	const setConfigurationUrl = (dynamicPart = 1) => {
		setSearchParams(
			(params) => {
				params.set('step', String(dynamicPart));
				return params;
			},
			{ preventScrollReset: true },
		);
	};

	const updateValidatedStepsMap = (currStep: number) => (hasError: boolean, reset?: boolean) => {
		if (reset) {
			setValidatedStepsMap(initialStepsValidationMap);
			return;
		}
		setValidatedStepsMap((prevErrorMap) =>
			prevErrorMap.map((currError, index) => (index === currStep ? Boolean(hasError) : currError)),
		);
	};

	const handleCartErrorCode = (res: ICartData) => {
		if (res.cartOverlappItems) {
			setValidationErrorCode(res.errors[0] ? res.errors[0].errorNumber : 60);
			setCartOverlapItems(res.cartOverlappItems ? res.cartOverlappItems : []);
			setModalIsOpen(true);
			return;
		}
		if (validationErrorCodes.includes(res.errors[0].errorNumber)) {
			setModalIsOpen(true);
			setValidationErrorCode(res.errors[0].errorNumber);
			return;
		}
		if (res.code === 401) {
			navigate(getUrlBasePath(`/session-expired${setRouteLangParam(lang)}`), { replace: true });
			deleteSelectedProducts();
		} else if (res.redirectToErrorPage) {
			redirectToErrorPage(res.errors);
			deleteSelectedProducts();
		} else {
			const errorText = getErrorTextFromResponse(res.errors, getLabelValue('mp.generalError', labels));
			setApiError(errorText);
		}
	};

	const handleCartUpdateResponse = (res: ICartData, offerHashes?: string[]) => {
		if (res.code === 200 && res.cart && res.token) {
			if (offerHashes) {
				const offers: IOffer[] = offerHashes.map((offerHash) =>
					getProductByOfferHash(offerHash, allOffers),
				) as IOffer[];
				gatherProductTrackingData({ offers, status: 'add' });
			}
			updateCart(res.cart);
			updateSelectedCartItem(res.cart.cartDetails[0]);
			sessionStorage.setItem('cartHash', res.cart.cartHash);
			sessionStorage.setItem('cartToken', res.token);
			const newProductCount = selectedProducts.some(({ moduleTypeId }) => moduleTypeId === 3)
				? selectedProducts.length - 1 + selectedSectionTolls.length
				: selectedProducts.length + selectedSectionTolls.length;
			sessionStorage.setItem('newProductsCount', JSON.stringify(newProductCount));
			const vignetteProduct = selectedProducts.find(({ moduleTypeId }) => moduleTypeId === 2);
			const moduleDetailId = vignetteProduct?.moduleTypeId || selectedProducts[0].moduleDetailId;
			const { licenseplate, licenseplateType, checkOutDateTime, countryCode } = getVignetteData(
				vignetteProduct || selectedProducts[0],
			);
			const upsellData: IUpsellRequestBody = {
				cartHash: res.cart.cartHash,
				moduleDetailId,
				checkOutDateTime: { ...checkOutDateTime, time: '00:00' },
				countryCode,
				licenseplate,
				licenseplateType,
			};
			sessionStorage.setItem('upsellData', JSON.stringify(upsellData));
			onFinished();
		} else {
			handleCartErrorCode(res);
		}
	};

	const getInitialData = async () => {
		const cart = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken');

		if (cart && token && cartItems.length === 0) {
			await CartService.getCartInfo({ body: { cartHash: cart }, token, lang }).then((res) => {
				if (res.code === 200 && res.cart) {
					updateCart(res.cart);
				} else {
					handleCartErrorCode(res);
				}
			});
		}
	};

	const focusOnSectionsFirstElement = (currentStep = step) => {
		const refSection = sectionsRefArray.current[currentStep];
		if (refSection) {
			const { firstFocusableElement } = getFocusableElements(refSection);
			firstFocusableElement?.focus({ preventScroll: true });
		}
	};

	const scrollToStep = (currentStep = step) => {
		const nextSection = sectionsRefArray.current[currentStep];
		if (nextSection) {
			currElRef.current = nextSection;
			focusOnSectionsFirstElement(currentStep);
			const additionalHeightWithCart = viewport === 'desktop' ? 184 : 0;
			const additionalHeight = viewport === 'desktop' ? HeaderHeights.M : -HeaderHeights.S;
			const margin = supplierHasMultipleProducts ? additionalHeightWithCart : additionalHeight;
			window.scrollTo({
				left: 0,
				top: nextSection.offsetTop + margin,
				behavior: 'smooth',
			});
		}
	};

	const formatAddingProductsToCartPayload = () => {
		const offers = selectedProducts
			.filter(({ moduleTypeId }) => moduleTypeId !== 3)
			.map((offer) => {
				const vignetteData = getVignetteData(offer);
				return {
					offerHash: offer.offerHash,
					licenseplateTypePublic: platePubliclyVisible,
					...vignetteData,
				};
			});
		const sectionTollOffers = selectedSectionTolls.map((offer) => {
			const vignetteData = getVignetteData(offer);
			return { offerHash: offer.offerHash, ...vignetteData };
		});
		return [...offers, ...sectionTollOffers];
	};

	const addVignetteToCart = async (
		cartHash: string,
		offerHash: string,
		vignetteData: IVignetteData,
		token: string,
		useracceptance?: boolean,
		licenseplateTypePublic?: boolean,
	) => {
		await CartService.insertItemToCart({
			body: {
				cartHash,
				offerHash,
				useracceptance,
				licenseplateTypePublic,
				...vignetteData,
			},
			token,
			lang,
		}).then((res) => handleCartUpdateResponse(res, [offerHash]));
	};

	const addMultipleVignettesToCart = async (
		cartHash: string,
		offers: IItemInfo[],
		token: string,
		useracceptance?: boolean,
	) => {
		await CartService.bulkInsertItemsToCart({
			body: {
				cartHash,
				offers,
				useracceptance,
			},
			token,
			lang,
		}).then((res) =>
			handleCartUpdateResponse(
				res,
				offers.map(({ offerHash }) => offerHash),
			),
		);
	};

	const handleVignettesWithCart = async (useracceptance?: boolean) => {
		const cartHash = sessionStorage.getItem('cartHash') ?? '';
		const token = sessionStorage.getItem('cartToken') ?? '';

		setIsLoading(true);
		if (isOneCartFlow || selectedSectionTolls.length > 0) {
			const offers = formatAddingProductsToCartPayload();
			await addMultipleVignettesToCart(cartHash, offers, token, useracceptance);
		} else {
			const vignetteData = getVignetteData(currSelectedProduct);
			await addVignetteToCart(
				cartHash,
				currSelectedProduct.offerHash,
				vignetteData,
				token,
				useracceptance,
				platePubliclyVisible,
			);
		}
		setIsLoading(false);
	};

	const handleNextStepUrlParam = (nextStep: number) => {
		if (currentUrlStepParam !== nextStep) {
			for (let i = currentUrlStepParam; i <= nextStep + 1; i++) {
				if (i === nextStep) setUrlStepParam(i);
				setConfigurationUrl(i);
			}
		} else {
			setConfigurationUrl(nextStep + 1);
			setUrlStepParam(nextStep + 1);
		}
	};

	const handleErrorStepUrlParam = () => {
		if (currentUrlStepParam !== errorIndex + 1) {
			for (let i = currentUrlStepParam; i <= errorIndex; i++) {
				if (i === errorIndex) setUrlStepParam(i + 1);
				setConfigurationUrl(i + 1);
			}
		}
	};

	const increaseStep = async () => {
		const nextStep = step + 1;
		if (hasErrorIndex) {
			scrollToStep(errorIndex);
			setErrorStepCount(errorIndex);
			handleErrorStepUrlParam();
			return;
		}
		if (step < lastStep) {
			setStep(nextStep);
			setUrlStepParam((prev) => prev + 1);
			handleNextStepUrlParam(nextStep);
			setTimeout(() => {
				scrollToStep(nextStep);
			}, 500);
		} else if (selectedProducts.length) {
			// Vignette Configurations
			await handleVignettesWithCart(true);
		} else {
			// DKV Configuration
			onFinished();
		}
	};

	const handleValidationErrorCodeOnSubmit = async (errorCode: number) => {
		if (hasFlexService || isOneCartFlow || validationErrorCodes.includes(errorCode)) {
			setModalIsOpen(false);
			return;
		}
		await handleVignettesWithCart();
	};

	const getComponentWithProps = (component: ReactElement, index: number) => {
		if (withStepReset && index === 0) {
			return React.cloneElement(component, {
				reuse,
				stepCount: step,
				hasError: errorStepCount === index,
				supplierHasMultipleProducts,
				updateValidatedStepsMap: updateValidatedStepsMap(index),
				updateStepCount: setStep,
				focusOnSectionsFirstElement,
			});
		}
		return React.cloneElement(component, {
			stepCount: step,
			hasError: errorStepCount === index,
			supplierHasMultipleProducts,
			updateValidatedStepsMap: updateValidatedStepsMap(index),
			updateStepCount: setStep,
			focusOnSectionsFirstElement,
		});
	};

	const getTotalSteps = () => (isOneCartFlow ? componentList.length - 2 : componentList.length - 1);

	// Automatic scroll functionality for deep linking
	useEffect(() => {
		let lastTrueIndexValue = -1;
		const dateIndex = componentList.findIndex(({ title }) => title === 'Gültigkeit');
		validatedStepsMap.forEach((steps, index) => {
			if (steps === true) {
				lastTrueIndexValue = index;
			}
		});
		const nextStep = lastTrueIndexValue + 1;
		// don't scroll further than "Start date" step
		if (lastTrueIndexValue >= 0 && autoScroll && (dateIndex === -1 || nextStep <= dateIndex)) {
			setStep(nextStep);
		}
		if (previousStep !== step && autoScroll) {
			scrollToStep(nextStep);
		}
		setTimeout(() => {
			setAutoscroll(false);
		}, 2000);
	}, [validatedStepsMap, autoScroll, step]);

	// Fetch BE data
	useEffect(() => {
		getInitialData();
	}, [lang]);

	// setValidatedStepsMap logic
	useEffect(() => {
		if (componentList.length > validatedStepsMap.length) {
			setValidatedStepsMap((prev) =>
				[...prev].concat(componentList.slice(validatedStepsMap.length).map(() => false)),
			);
		} else if (componentList.length < validatedStepsMap.length) {
			setValidatedStepsMap((prev) => {
				if (!prev[componentList.length - 1]) {
					const arr = [...prev];
					arr.splice(arr.indexOf(false), 1);
					return arr;
				}
				return [...prev].slice(0, componentList.length - 1);
			});
		}
	}, [componentList, validatedStepsMap]);

	// Navigate logic
	useEffect(() => {
		if (isOneCartFlow && !selectedCountries.length && step > 1) {
			setStep(1);
			if (currentUrlStepParam > 2) {
				setConfigurationUrl(2);
			}
		}
	}, [selectedCountries, step]);

	// Navigate logic
	// TODO: Could be combined in previous useEffect?
	useEffect(() => {
		if (!step && currentUrlStepParam > 1) {
			setConfigurationUrl();
		}
	}, [step]);

	// Reset logic
	useEffect(() => {
		if (isDkvFlow && !vehicleRegistration) {
			setStep(0);
		}
	}, [vehicleRegistration, isDkvFlow]);

	// setErrorStepCount logic
	useEffect(() => {
		const stepWithError = validatedStepsMap.findIndex((bool) => !bool);
		if (stepWithError === -1 || stepWithError !== errorStepCount) {
			setErrorStepCount(undefined);
		}
	}, [errorStepCount, validatedStepsMap]);

	const handlePopState = () => {
		scrollToStep(urlStepParam - 2);
		setUrlStepParam((prev) => (prev === 1 ? 1 : prev - 1));
	};

	// Dynamic url functionality setup and cleanup
	useEffect(() => {
		window.history.scrollRestoration = 'manual';
		window.addEventListener('popstate', handlePopState);
		return () => {
			window.history.scrollRestoration = 'auto';
			window.removeEventListener('popstate', handlePopState);
		};
	}, [urlStepParam]);

	return (
		<>
			<ContentLoader isLoading={isLoading} />
			{supplierHasMultipleProducts && (
				<MiniCartIcon
					step={step}
					currStep={calculateCurrentStep(
						productsTypeMap,
						selectedSectionTolls,
						step,
						lastStep,
						isOneCartFlow,
						reuse,
					)}
					totalSteps={getTotalSteps()}
					cartItemsCount={cartItems.length}
					setIsOpen={setMiniCartIsOpen}
				/>
			)}
			<div
				className={`${
					supplierHasMultipleProducts
						? `transition-translate duration-300 -mt-[52px] ${
								miniCartIsOpen
									? '-translate-x-[calc(100%_-_24px)] md:-translate-x-[256px] pointer-events-none md:pointer-events-auto'
									: 'translate-x-0'
						  }`
						: ''
				}`}>
				{componentList.map(
					(component, index) =>
						step >= index && (
							<section
								key={index}
								ref={(element) => (element ? (sectionsRefArray.current[index] = element) : undefined)}
								className={`${index > 0 ? 'md:pt-10' : ''} ${
									index === step ? 'min-h-[calc(100vh-126px)] md:min-h-0' : ''
								}`}
								id={`step${index}`}>
								{getComponentWithProps(component.component, index)}
							</section>
						),
				)}
				<FooterButtonNav
					data-cy="footer-next-button"
					reuse={reuse}
					step={step}
					lastStep={lastStep}
					validated={!hasErrorIndex}
					stepTitle={componentList[step]?.title}
					onClick={increaseStep}
					onReuseClick={() => {
						deleteSelectedProducts();
						updateVehicleCategory(undefined);
						setReuse(false);
					}}
				/>
				<Contact />
			</div>
			{supplierHasMultipleProducts && (
				<CartDrawer
					reuse={reuse}
					currStep={step}
					totalSteps={componentList.length - 1}
					cartPrice={cartPrice}
					miniCartIsOpen={miniCartIsOpen}
					setMiniCartIsOpen={setMiniCartIsOpen}
					scrollToStep={(currStep) => {
						const currUrlStepIndex = currentUrlStepParam - 1;
						if (currStep !== currUrlStepIndex && currStep) {
							setConfigurationUrl(currStep + 1);
						}
						scrollToStep(currStep);
					}}
				/>
			)}
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
			{validationErrorCode && (
				<CartAndSystemValidationModal
					errorCode={validationErrorCode}
					cartOverlapItems={cartOverlapItems}
					isOpen={modalIsOpen}
					onSubmit={async () => await handleValidationErrorCodeOnSubmit(validationErrorCode)}
					onCancel={() => {
						if (cartItems.length > 0) {
							navigate(
								getUrlBasePath(`/${countryUrlMap[supplierId]}/product-done${setRouteLangParam(lang)}`),
							);
						} else {
							navigate(getUrlBasePath(`/empty-cart${setRouteLangParam(lang)}`), { replace: true });
						}
					}}
					onClose={() => setModalIsOpen(false)}
				/>
			)}
		</>
	);
};

export default Configuration;
